/**
 * Who the SERVER says this browser is.
 *
 * The console never decides its own permissions. It asks /api/me, renders from the answer,
 * and every write is enforced again by the middleware in serving/app.py -- so a user who
 * edits `is_admin` in devtools gets the settings tab drawn and a 403 the moment they touch
 * anything. Hiding a control is a courtesy; the server is the rule.
 */
import { useEffect, useState } from 'react'

export interface Session {
  signed_in: boolean
  email?: string
  name?: string
  is_admin?: boolean
  username?: string
  role?: string
  /** '' for nobody assigned yet -- same "fails closed" rule the server applies. */
  department?: string
  sites?: string[]
  /** Server flag: when true an account with no department is DENIED, not left open. */
  rbac_require_grant?: boolean
  /** 'disabled' when the deployment has no login configured -- the laptop stack. Otherwise
   *  which door this session actually came through: 'entra' | 'local' | 'sso'. */
  auth?: string
  domain?: string
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'anonymous'; domain: string; error?: string }
  | { status: 'signed-in'; session: Session }

const SSO_PARAM = 'sso'

/** Exchanges a handoff token for a session cookie, if one arrived in the URL.
 *
 * A 60-SECOND CREDENTIAL MUST NOT LINGER. The token is stripped from the address bar via
 * history.replaceState the moment this returns, success or failure -- otherwise it sits in
 * browser history and in the Referer header of the next request this page makes, both well
 * past the window it is actually valid for.
 *
 * Returns an error string on failure so the caller can show it, never throws: a bad or
 * expired handoff must fall through to the ordinary login page, not dead-end the app.
 */
async function exchangeHandoffToken(): Promise<string | undefined> {
  const params = new URLSearchParams(window.location.search)
  const token = params.get(SSO_PARAM)
  if (!token) return undefined

  let error: string | undefined
  try {
    const r = await fetch('/api/auth/sso-handoff', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
    if (!r.ok) {
      const body = (await r.json().catch(() => null)) as { error?: string } | null
      error = body?.error || 'sign-in link expired or was already used'
    }
  } catch {
    error = 'sign-in link could not be verified'
  }

  params.delete(SSO_PARAM)
  const rest = params.toString()
  const next = window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash
  window.history.replaceState({}, '', next)
  return error
}

/** The actual /api/me + SSO-handoff dance, run exactly once per page load however many
 *  components call useSession() -- confirmed live 2026-10-10: this ran independently
 *  from 13 different components (AppShell, every site's own shell, SettingsShell,
 *  ProfileSection, LoginPage, ...), each paying TWO sequential /api/me round trips on
 *  mount, compounding every site's page-load latency behind the browser's own
 *  per-origin connection limit. Resolves, never rejects: a network failure falls
 *  through to the same "treat as signed in" fallback the original per-component catch
 *  used, so one dropped request cannot throw away a working session for every
 *  consumer at once. */
async function loadSession(): Promise<SessionState> {
  try {
    // Already signed in? Do not spend a handoff token that is not needed -- a reopened
    // tab or a double-click on the same link must not burn a nonce for nothing.
    const already = await fetch('/api/me', { credentials: 'same-origin' }).then((r) => r.json())
    if (!already.signed_in && already.auth !== 'disabled') {
      const handoffError = await exchangeHandoffToken()
      if (handoffError) {
        return { status: 'anonymous', domain: already.domain || 'transvolt.in', error: handoffError }
      }
    }
    // Re-fetch: either nothing needed exchanging, or the exchange above just set a fresh
    // session cookie -- either way /api/me is the answer, never the exchange response
    // itself, matching this file's own rule that only the server decides.
    const s: Session = await fetch('/api/me', { credentials: 'same-origin' }).then((r) => r.json())
    // auth === 'disabled' means this deployment has no login configured, which is the
    // laptop stack and every environment before it is wired. Treated as signed in so
    // adding this file cannot lock anybody out of a console already running.
    if (s.signed_in || s.auth === 'disabled') {
      return { status: 'signed-in', session: { ...s, signed_in: true } }
    }
    return { status: 'anonymous', domain: s.domain || 'transvolt.in' }
  } catch {
    // A network failure is not a sign-out. Falling through to the login page here would
    // throw a working session away on one dropped request.
    return { status: 'signed-in', session: { signed_in: true, is_admin: true, auth: 'unknown' } }
  }
}

let sessionPromise: Promise<SessionState> | null = null

function getSessionPromise(): Promise<SessionState> {
  if (!sessionPromise) sessionPromise = loadSession()
  return sessionPromise
}

/** Login (local-login's own reload(), Entra's redirect round trip) and logout (TopBar's
 *  plain `<a href="/auth/logout">`) are real document navigations, not pushState --
 *  which means the document on either side of that boundary is a bfcache candidate. A
 *  user who logs out and hits Back can get the PRE-logout document repainted straight
 *  from the cache: same JS heap, same `sessionPromise` already resolved to "signed-in",
 *  no network request, no re-render -- the authenticated page back onscreen with a
 *  revoked cookie. The reverse (Forward into a pre-login "anonymous" snapshot while the
 *  cookie is actually valid) is the same bug the other direction. `pageshow` with
 *  `persisted: true` fires ONLY on exactly that restore -- never on React Router's own
 *  in-page pushState navigation -- so this cannot turn every Back into a full reload;
 *  it only forces the one re-check that was skipped when the browser skipped loadSession
 *  entirely. */
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) window.location.reload()
  })
}

export function useSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    getSessionPromise().then((s) => { if (!cancelled) setState(s) })
    return () => { cancelled = true }
  }, [])

  return state
}

/** Name, then email, then username -- whichever the door this session came through
 *  actually gave us. undefined (not '') so TopBar's `user ? ... : null` hides the
 *  avatar/logout pair cleanly when there is nobody signed in to show. */
export function sessionDisplayName(state: SessionState): string | undefined {
  if (state.status !== 'signed-in') return undefined
  const s = state.session
  return s.name || s.email || s.username || undefined
}
